import React from "react";
import { StyleSheet, View } from "react-native";
import moment from "moment";

import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import { kStyleConst } from "src/kStyle";
import { PdSelect, tPdSelectOption } from "src/components/v2/forms/select";
import { useIsMobile } from "src/hooks/ui";
import { PdStack, PdText } from "src/components/layout";
import { reportTypes } from "src/util/comboBoxData";
import { useExportReportsModal } from "src/hooks/modals";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";

import BrandsTable from "./BrandsTable";
import RetailersTable from "./RetailersTable";
import SalesRepsTable from "./SalesRepsTable";
import TotalSalesBySkuTable from "./TotalSalesBySkuTable";
import TotalSalesTable from "./TotalSalesTable";
import AbandonedCartsTable from "./AbandonedCartsTable";
import BrandInvitationsTable from "./BrandInvitationsTable";
import ConnectionsTable from "./ConnectionsTable";
import NewProductsTable from "./NewProductsTable";
import ProductsInventoryTable from "./ProductsInventoryTable";
import PromotionsTable from "./PromotionsTable";
import OldPromotionsTable from "./OldPromotionsTable";
import { useAppDispatch } from "src/redux";
import { PdDateRangeInput } from "src/components/v2/forms/input";

const ContentReport: React.FC<{ reportType: string; period: Date[] }> = ({ reportType, period }) => {
  if (reportType === "total-sales") {
    return <TotalSalesTable period={period} />;
  }

  if (reportType === "sales-by-sku") {
    return <TotalSalesBySkuTable period={period} />;
  }

  if (reportType === "sales-reps") {
    return <SalesRepsTable period={period} />;
  }

  if (reportType === "brands") {
    return <BrandsTable period={period} />;
  }

  if (reportType === "retailers") {
    return <RetailersTable period={period} />;
  }

  if (reportType === "connections") {
    return <ConnectionsTable period={period} />;
  }

  if (reportType === "brand-invites") {
    return <BrandInvitationsTable period={period} />;
  }

  if (reportType === "new-products") {
    return <NewProductsTable period={period} />;
  }

  if (reportType === "products-inventory") {
    return <ProductsInventoryTable period={period} />;
  }

  if (reportType === "abandoned-carts") {
    return <AbandonedCartsTable period={period} />;
  }

  if (reportType === "promotions") {
    return <PromotionsTable period={period} />;
  }

  if (reportType === "old-promotions") {
    return <OldPromotionsTable />;
  }

  return null;
};

const P11_Reports: React.FC = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [reportType, setReportType] = React.useState(reportTypes[0]);
  const [period, setPeriod] = React.useState<[Date, Date]>([moment().subtract(1, "month").toDate(), moment().toDate()]);
  const exportReportsModal = useExportReportsModal();

  const handleOnSelect = (option: tPdSelectOption | null) => {
    if (option) setReportType(option);
  };

  const handleOnChangePeriod = (dates: [Date, Date]) => {
    setPeriod(dates);
  };

  const handleOnExportReports = () => {
    exportReportsModal.openAsync({ reportType, period });
  };

  React.useEffect(() => {
    dispatch(setBackButtonRoute(undefined));
  }, [dispatch]);

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">Reports</PdText>
        {!isMobile && (
          <PdButton onPress={handleOnExportReports} colorScheme="green">
            <PdButtonLabel>Export Reports</PdButtonLabel>
          </PdButton>
        )}
      </PdStack>
      <PdStack layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"} style={styles.filters}>
        <PdSelect value={reportType} onChange={handleOnSelect} options={reportTypes} />
        <PdDateRangeInput
          period={[period[0], period[1]]}
          onChange={handleOnChangePeriod}
          isDisabled={reportType.value === "old-promotions"}
        />
      </PdStack>
      <ContentReport reportType={reportType.value} period={period} />
    </View>
  );
};

export const P11 = React.memo(P11_Reports);

const styles = StyleSheet.create({
  filters: {
    marginBottom: kStyleConst.kSpacing * 10,
    zIndex: 2,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  page: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
});
